import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import appConstants from '../constants/appConstants';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import {
  Tab,
  Nav,
  Form,
  Button,
  Modal,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Accordion,
} from 'react-bootstrap';
import './page.css';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentSummaryV3 = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const { selectedAcademicYear } = useAppContext();

  // Student data
  const [student, setStudent] = useState(null);
  const [studentBatches, setStudentBatches] = useState([]);
  const [batchHistory, setBatchHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Attendance
  const [attendanceMonth, setAttendanceMonth] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [batchAttendances, setBatchAttendances] = useState({});
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Notes
  const [notes, setNotes] = useState([]); // Flattened notes for calendar lookup
  const [batchNotes, setBatchNotes] = useState([]); // Batch-organized notes for Notes tab
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);

  // Test Marks
  const [testMarks, setTestMarks] = useState({});
  const scrollRefs = useRef({});

  // Exam Marks
  const [examTerms, setExamTerms] = useState([]);
  const [examSubjects, setExamSubjects] = useState([]);
  const [loadingExamMarks, setLoadingExamMarks] = useState(false);

  // Fee Data
  const [feeData, setFeeData] = useState({
    prevBalance: 0,
    thisYearFee: 0,
    totalPaid: 0,
    totalReturn: 0,
  });
  const [transactions, setTransactions] = useState([]);

  // Installment Fee Data
  const [installmentFeeData, setInstallmentFeeData] = useState(null);
  const [loadingInstallmentFees, setLoadingInstallmentFees] = useState(false);

  // Fee Adjustment Dashboard Data
  const [feeAdjustmentData, setFeeAdjustmentData] = useState(null);
  const [loadingFeeAdjustments, setLoadingFeeAdjustments] = useState(false);

  const [activeTab, setActiveTab] = useState('overview');

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(2025, i).toLocaleString('default', { month: 'long' }),
  );

  // Generate batch colors
  const generateBatchColor = (index) => {
    // Using single color scheme for all batches - soft blue-gray for clean, professional look
    return { bg: '#eceff1', border: '#607d8b', text: '#263238' };
  };

  useEffect(() => {
    fetchStudentData();
  }, [studentId, selectedAcademicYear]);

  useEffect(() => {
    if (studentId) {
      loadAttendance(attendanceMonth);
    }
  }, [attendanceMonth, studentId, selectedAcademicYear]);

  useEffect(() => {
    if (activeTab === 'academics' && studentId) {
      loadTestMarks();
      fetchExamMarks();
    }
    if (activeTab === 'installmentFees' && studentId && selectedAcademicYear) {
      loadInstallmentFeeDetails();
    }
    if (activeTab === 'feeAdjustments' && studentId && selectedAcademicYear) {
      loadFeeAdjustmentDetails();
    }
  }, [activeTab, studentId, selectedAcademicYear]);

  useEffect(() => {
    Object.values(scrollRefs.current).forEach((container) => {
      if (container) {
        container.scrollLeft = container.scrollWidth;
      }
    });
  }, [testMarks]);

  useEffect(() => {
    const totalPaid = transactions
      .filter((txn) => txn.txn_type === 'C')
      .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);

    const totalReturn = transactions
      .filter((txn) => txn.txn_type === 'D')
      .reduce((sum, txn) => sum + parseFloat(txn.amount), 0);

    setFeeData((prevState) => ({
      ...prevState,
      totalPaid,
      totalReturn,
    }));
  }, [transactions]);

  const fetchStudentData = async () => {
    setLoading(true);

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setLoading(false);
      console.error('No academic year selected');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}student-summary-v3`,
        {
          student_id: studentId,
          academic_year_id: selectedAcademicYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        },
      );

      if (response.data.status === 'success') {
        const { student, batches, batch_history, batch_notes } = response.data;

        setStudent(student);
        setStudentBatches(batches || []);
        setBatchHistory(batch_history || []);
        setBatchNotes(batch_notes || []);

        // Flatten batch_notes for calendar lookup
        const flattenedNotes = [];
        (batch_notes || []).forEach((batchNote) => {
          (batchNote.notes || []).forEach((note) => {
            flattenedNotes.push({
              ...note,
              batch_id: batchNote.batch_id,
              class_name: batchNote.class_name,
              division: batchNote.division,
            });
          });
        });
        setNotes(flattenedNotes);

        // Load all other data
        loadTestMarks();
        loadFeeDetails();
      }
    } catch (error) {
      console.error('Error fetching student data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async ({ month, year }) => {
    setLoadingAttendance(true);

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setLoadingAttendance(false);
      console.error('No academic year selected for attendance');
      return;
    }

    const token = getAuthToken();
    const monthStr = month.toString().padStart(2, '0');
    const date = `${year}-${monthStr}-01`;

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-attendance-v3`,
        {
          student_id: studentId,
          date,
          academic_year_id: selectedAcademicYear.id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const batchAttendanceData = {};
      response.data.attendance.forEach((batch) => {
        batchAttendanceData[batch.batch_id] = {
          ...batch,
          attendance: batch.attendance,
        };
      });

      setBatchAttendances(batchAttendanceData);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const loadTestMarks = async () => {
    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      console.error('No academic year selected for test marks');
      return;
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-test-marks-v2`,
        {
          student_id: studentId,
          academic_year_id: selectedAcademicYear.id,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        },
      );

      if (response.data.status === 'success') {
        const grouped = {};
        response.data.data.forEach((mark) => {
          const subject = mark.subject_name;
          if (!grouped[subject]) {
            grouped[subject] = [];
          }
          grouped[subject].push(mark);
        });
        setTestMarks(grouped);
      }
    } catch (error) {
      console.error('Error fetching test marks:', error);
    }
  };

  const loadFeeDetails = async () => {
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-fee-details`,
        { student_id: studentId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        },
      );
      setTransactions(response.data.data.transactions);
      setFeeData({
        prevBalance: response.data.data.prevBalance,
        thisYearFee: response.data.data.thisYearFee,
        totalPaid: 0,
        totalReturn: 0,
      });
    } catch (error) {
      console.error('Error fetching fee details:', error);
    }
  };

  const loadInstallmentFeeDetails = async () => {
    setLoadingInstallmentFees(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_student_fee_dashboard?student_id=${studentId}&academic_year_id=${selectedAcademicYear.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data.status === 'success') {
        setInstallmentFeeData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading installment fee details:', error);
    } finally {
      setLoadingInstallmentFees(false);
    }
  };

  const loadFeeAdjustmentDetails = async () => {
    setLoadingFeeAdjustments(true);
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get_student_fee_adjustment_dashboard?student_id=${studentId}&academic_year_id=${selectedAcademicYear.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.status === 'success') {
        setFeeAdjustmentData(response.data.data);
      } else {
        setFeeAdjustmentData(null);
      }
    } catch (error) {
      console.error('Error loading fee adjustment details:', error);
      setFeeAdjustmentData(null);
    } finally {
      setLoadingFeeAdjustments(false);
    }
  };

  const fetchExamMarks = async () => {
    setLoadingExamMarks(true);

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setLoadingExamMarks(false);
      console.error('No academic year selected for exam marks');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');

      // Fetch student summary data and subjects list
      const [summaryRes, subjectsRes] = await Promise.all([
        axios.post(
          `${appConstants.API_ROOT_URL}student-summary-v3`,
          {
            student_id: studentId,
            academic_year_id: selectedAcademicYear.id,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        axios.get(`${appConstants.API_ROOT_URL}get-subjects`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (
        summaryRes.data.status === 'success' &&
        summaryRes.data.marks &&
        Array.isArray(summaryRes.data.marks)
      ) {
        // Create subject name mapping
        const subjectNameMap = new Map();
        if (subjectsRes.data.status === 'success' && subjectsRes.data.data) {
          subjectsRes.data.data.forEach((subject) => {
            subjectNameMap.set(subject.id, subject.name);
          });
        }

        // Extract all unique term exams across all batches
        const termExamsMap = new Map();
        const subjectMarksMap = new Map();

        summaryRes.data.marks.forEach((batchData) => {
          if (batchData.exams && Array.isArray(batchData.exams)) {
            batchData.exams.forEach((exam) => {
              const examKey = exam.exam_name;

              // Store exam info with evaluation type from first subject
              if (
                !termExamsMap.has(examKey) &&
                exam.subjects &&
                exam.subjects.length > 0
              ) {
                termExamsMap.set(examKey, {
                  exam_id: exam.exam_id,
                  evaluation_type: exam.subjects[0].evaluation_type,
                });
              }

              if (exam.subjects && Array.isArray(exam.subjects)) {
                exam.subjects.forEach((subject) => {
                  const subjectKey = subject.subject_id;
                  if (!subjectMarksMap.has(subjectKey)) {
                    subjectMarksMap.set(subjectKey, {
                      subject_id: subject.subject_id,
                      subject_name:
                        subjectNameMap.get(subject.subject_id) ||
                        `Subject ${subject.subject_id}`,
                      marks: {},
                    });
                  }

                  const subjectData = subjectMarksMap.get(subjectKey);

                  // Store mark data with evaluation type
                  subjectData.marks[examKey] = {
                    marks_obtained: subject.marks_obtained,
                    max_mark: subject.max_mark,
                    evaluation_type: subject.evaluation_type,
                  };
                });
              }
            });
          }
        });

        // Get all term exam info
        const termExamInfo = Array.from(termExamsMap.entries()).map(
          ([name, info]) => ({
            name,
            evaluation_type: info.evaluation_type,
          }),
        );

        // Convert subject marks map to array and calculate totals
        const processedSubjects = Array.from(subjectMarksMap.values());

        // Add total row for mark-based exams
        termExamInfo.forEach((examInfo) => {
          if (examInfo.evaluation_type === 'mark') {
            let totalObtained = 0;
            let totalMax = 0;
            let hasData = false;

            processedSubjects.forEach((subject) => {
              const markData = subject.marks[examInfo.name];
              if (
                markData &&
                markData.marks_obtained !== null &&
                markData.max_mark !== null
              ) {
                totalObtained += parseFloat(markData.marks_obtained) || 0;
                totalMax += parseFloat(markData.max_mark) || 0;
                hasData = true;
              }
            });

            if (hasData) {
              processedSubjects.push({
                subject_id: 'total_' + examInfo.name,
                subject_name: 'Total',
                marks: {
                  [examInfo.name]: {
                    marks_obtained: totalObtained,
                    max_mark: totalMax,
                    evaluation_type: 'mark',
                  },
                },
              });
            }
          }
        });

        setExamTerms(termExamInfo);
        setExamSubjects(processedSubjects);
      } else {
        setExamTerms([]);
        setExamSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching exam marks:', error);
      setExamTerms([]);
      setExamSubjects([]);
    } finally {
      setLoadingExamMarks(false);
    }
  };

  const handleSaveNote = async () => {
    if (!studentId) return;
    const token = getAuthToken();
    if (!token) {
      alert('Unauthorized! Please log in.');
      return;
    }

    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}create-student-note-v2`,
        {
          student_id: parseInt(studentId),
          batch_id: currentBatchId,
          date: noteDate,
          note: currentNote,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Reload notes
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-one-student-notes-v2`,
        { student_id: studentId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const batch_notes = response.data.batch_notes || [];
      setBatchNotes(batch_notes);

      // Flatten for calendar lookup
      const flattenedNotes = [];
      batch_notes.forEach((batchNote) => {
        (batchNote.notes || []).forEach((note) => {
          flattenedNotes.push({
            ...note,
            batch_id: batchNote.batch_id,
            class_name: batchNote.class_name,
            division: batchNote.division,
          });
        });
      });
      setNotes(flattenedNotes);
      setShowNoteModal(false);
      setCurrentNote('');
      setNoteDate('');
      setEditingNoteId(null);
    } catch (error) {
      console.error('Failed to save note', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Error saving note';
      alert(errorMessage);
    }
  };

  const handleDeleteNote = async (note, batchId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    const token = getAuthToken();
    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}create-student-note-v2`,
        {
          student_id: parseInt(studentId),
          batch_id: batchId,
          date: note.note_date,
          note: '',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-one-student-notes-v2`,
        { student_id: studentId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const batch_notes = response.data.batch_notes || [];
      setBatchNotes(batch_notes);

      // Flatten for calendar lookup
      const flattenedNotes = [];
      batch_notes.forEach((batchNote) => {
        (batchNote.notes || []).forEach((note) => {
          flattenedNotes.push({
            ...note,
            batch_id: batchNote.batch_id,
            class_name: batchNote.class_name,
            division: batchNote.division,
          });
        });
      });
      setNotes(flattenedNotes);
    } catch (error) {
      console.error('Failed to delete note', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to delete note';
      alert(errorMessage);
    }
  };

  const changeMonth = (direction) => {
    setAttendanceMonth((prev) => {
      let month = prev.month + direction;
      let year = prev.year;

      if (month > 12) {
        month = 1;
        year += 1;
      } else if (month < 1) {
        month = 12;
        year -= 1;
      }

      return { month, year };
    });
  };

  // Group batch history by unique batch_id (same batch may have multiple periods)
  const getUniqueBatches = () => {
    const uniqueBatchMap = new Map();

    batchHistory.forEach((batch) => {
      if (!uniqueBatchMap.has(batch.batch_id)) {
        uniqueBatchMap.set(batch.batch_id, {
          batch_id: batch.batch_id,
          class_name: batch.class_name,
          division: batch.division,
          periods: [],
        });
      }
      uniqueBatchMap.get(batch.batch_id).periods.push({
        student_batch_id: batch.student_batch_id,
        assigned_date: batch.assigned_date,
        end_date: batch.end_date,
      });
    });

    return Array.from(uniqueBatchMap.values());
  };

  // Check if a date falls within any period of a specific batch
  const isDateInBatch = (date, batchPeriods) => {
    const dateStr = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    return batchPeriods.some((period) => {
      const assignedDate = period.assigned_date;
      const endDate = period.end_date || '9999-12-31';
      return dateStr >= assignedDate && dateStr <= endDate;
    });
  };

  const renderBatchCalendar = (batchInfo, batchIndex) => {
    const year = attendanceMonth.year;
    const month = attendanceMonth.month;
    const totalDays = new Date(year, month, 0).getDate();
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const today = new Date();

    const isToday = (day) =>
      today.getDate() === day &&
      today.getMonth() + 1 === month &&
      today.getFullYear() === year;

    const color = generateBatchColor(batchIndex);
    const batchAtt = batchAttendances[batchInfo.batch_id];

    const calendarCells = [];

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      calendarCells.push(
        <div
          key={`empty-${i}`}
          style={{
            minHeight: '50px',
            border: '1px solid transparent',
          }}
        />,
      );
    }

    // Calendar days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month - 1, day);
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(
        day,
      ).padStart(2, '0')}`;
      const note = notes.find(
        (n) => n.note_date === dateStr && n.batch_id === batchInfo.batch_id,
      );
      const isInBatch = isDateInBatch(date, batchInfo.periods);

      // Check attendance status regardless of batch date frame
      let attendanceStatus = 'N';
      if (batchAtt && batchAtt.attendance && batchAtt.attendance[day]) {
        attendanceStatus = batchAtt.attendance[day].status;
      }

      // Create tooltip - show only note if it exists
      let tooltip = note ? note.note : '';

      let bgColor = '#fff';
      let borderColor = '#e0e0e0';

      // Show Present in green regardless of batch date frame
      if (attendanceStatus === 'P') {
        bgColor = '#c8e6c9'; // Light green for Present
        borderColor = isInBatch ? color.border : '#4caf50';
      } else if (isInBatch) {
        borderColor = color.border;

        if (attendanceStatus === 'A') {
          bgColor = '#ffcdd2'; // Light red for Absent
        } else {
          bgColor = color.bg; // Batch color for no attendance data
        }
      }
      const cellContent = (
        <div
          className="calendar-day-cell"
          style={{
            minHeight: '35px',
            border: isToday(day)
              ? '2px solid #000'
              : `1px solid ${borderColor}`,
            borderRadius: '3px',
            padding: '2px',
            background: bgColor,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            opacity: isInBatch || attendanceStatus === 'P' ? 1 : 0.3,
          }}
          onClick={() => {
            setNoteDate(dateStr);
            setCurrentNote(note?.note || '');
            setEditingNoteId(note?.id || null);
            setCurrentBatchId(batchInfo.batch_id);
            setShowNoteModal(true);
          }}
        >
          <div
            style={{
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: isInBatch || attendanceStatus === 'P' ? '#333' : '#999',
            }}
          >
            {day}
          </div>

          {/* Note indicator */}
          {note && (
            <div
              style={{
                position: 'absolute',
                bottom: '1px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '8px',
                height: '2px',
                background: '#ff5722',
                borderRadius: '1px',
              }}
            />
          )}
        </div>
      );

      calendarCells.push(
        note ? (
          <OverlayTrigger
            key={day}
            placement="top"
            overlay={<Tooltip>{note.note}</Tooltip>}
          >
            {cellContent}
          </OverlayTrigger>
        ) : (
          cellContent
        ),
      );
    }

    return (
      <div
        key={batchInfo.batch_id}
        className="mb-3 batch-calendar-container"
        style={{
          border: `2px solid ${color.border}`,
          borderRadius: '8px',
          padding: '8px',
          background: '#fafafa',
        }}
      >
        {/* Batch Header */}
        <div
          className="mb-2 d-flex justify-content-between align-items-center flex-wrap"
          style={{
            background: color.bg,
            padding: '6px 8px',
            borderRadius: '6px',
            border: `1px solid ${color.border}`,
          }}
        >
          <strong style={{ color: color.text, fontSize: '0.85rem' }}>
            {batchInfo.class_name} {batchInfo.division}
          </strong>
          <div style={{ fontSize: '0.7rem', color: color.text }}>
            {batchInfo.periods.map((p, idx) => (
              <div key={idx}>
                {p.assigned_date} to {p.end_date || 'Present'}
              </div>
            ))}
          </div>
        </div>

        {/* Day Headers */}
        <div
          className="calendar-day-headers"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
            marginBottom: '2px',
          }}
        >
          {daysOfWeek.map((day, index) => (
            <div
              key={`day-header-${index}`}
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '0.65rem',
                padding: '2px',
                background: color.bg,
                color: color.text,
                borderRadius: '2px',
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div
          className="calendar-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
          }}
        >
          {calendarCells}
        </div>

        {/* Batch Stats */}
        {batchAtt && (
          <div
            className="mt-2 pt-2 batch-stats"
            style={{
              borderTop: `1px solid ${color.border}`,
              fontSize: '0.7rem',
              color: '#666',
            }}
          >
            {(() => {
              const presentDays = Object.values(
                batchAtt.attendance || {},
              ).filter((d) => d.status === 'P').length;
              const absentDays = Object.values(
                batchAtt.attendance || {},
              ).filter((d) => d.status === 'A').length;
              const totalDays = presentDays + absentDays;
              const percentage =
                totalDays > 0
                  ? ((presentDays / totalDays) * 100).toFixed(1)
                  : 0;

              return (
                <div className="d-flex justify-content-around flex-wrap">
                  <span className="me-2 mb-1">
                    <strong>{presentDays}</strong> P
                  </span>
                  <span className="me-2 mb-1">
                    <strong>{absentDays}</strong> A
                  </span>
                  <span className="mb-1">
                    <strong>{percentage}%</strong>
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderEnhancedAttendanceCalendar = () => {
    const uniqueBatches = getUniqueBatches();

    if (uniqueBatches.length === 0) {
      return (
        <div className="text-center text-muted py-5">
          No batch history available
        </div>
      );
    }

    return (
      <div>
        {/* Legend */}
        <div className="mb-3 d-flex flex-wrap gap-3 align-items-center">
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                background: '#c8e6c9',
                marginRight: '6px',
              }}
            />
            <small>Present</small>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                background: '#ffcdd2',
                marginRight: '6px',
              }}
            />
            <small>Absent</small>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '20px',
                height: '2px',
                background: '#ff5722',
                marginRight: '6px',
              }}
            />
            <small>Has Note</small>
          </div>
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '20px',
                height: '20px',
                background: '#fff',
                border: '1px solid #ddd',
                marginRight: '6px',
                borderRadius: '4px',
                opacity: 0.3,
              }}
            />
            <small>Not in Batch</small>
          </div>
        </div>

        {/* Batch Calendars in Grid */}
        <div
          className="batch-calendars-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            minHeight: loadingAttendance ? '420px' : undefined,
            transition: 'min-height 0.2s',
          }}
        >
          {uniqueBatches.map((batch, idx) => renderBatchCalendar(batch, idx))}
        </div>
      </div>
    );
  };

  if (loading)
    return (
      <Layout>
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );

  if (!student)
    return (
      <Layout>
        <div className="text-center mt-5">Student not found</div>
      </Layout>
    );

  const studentStatus = student?.status || 'Unknown';
  const statusVariant =
    studentStatus === 'Active'
      ? 'success'
      : studentStatus === 'Suspend'
        ? 'danger'
        : 'secondary';
  const statusLabel = studentStatus === 'Suspend' ? 'Suspended' : studentStatus;

  return (
    <Layout>
      <div className="container-fluid">
        {/* Header Section */}
        <div className="row mb-3">
          <div className="col-12">
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                    <OverlayTrigger
                      placement="right"
                      trigger={['hover', 'focus']}
                      overlay={
                        <div
                          style={{
                            position: 'absolute',
                            top: '0',
                            backgroundColor: 'white',
                            border: '2px solid #2196f3',
                            borderRadius: '8px',
                            padding: '5px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 1050,
                          }}
                        >
                          <img
                            src={
                              student.photo
                                ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
                                : `${appConstants.API_ROOT_URL}uploads/students/blank.png`
                            }
                            alt="Student"
                            style={{
                              width: '250px',
                              height: '250px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              display: 'block',
                            }}
                          />
                        </div>
                      }
                    >
                      <img
                        src={
                          student.photo
                            ? `${appConstants.API_ROOT_URL}uploads/students/${student.photo}`
                            : `${appConstants.API_ROOT_URL}uploads/students/blank.png`
                        }
                        alt="Student"
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          border: '3px solid #2196f3',
                          cursor: 'pointer',
                        }}
                      />
                    </OverlayTrigger>
                    <div>
                      <h4 className="mb-1">{student.name}</h4>
                      <div className="text-muted d-flex flex-wrap align-items-center gap-2">
                        <span>
                          {student.admission_no ||
                            `Appl: ${student.application_no}`}
                        </span>
                        <Badge bg={statusVariant} className="px-2 py-1">
                          {statusLabel}
                        </Badge>
                      </div>
                      <div className="mt-1">
                        {studentBatches.map((b, idx) => (
                          <Badge
                            key={b.student_batch_id}
                            bg="primary"
                            className="me-1"
                          >
                            {b.class_name} - {b.division}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate(`/student-view/${student.id}`)}
                    >
                      <i className="fas fa-user me-2"></i>
                      Full Profile
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() =>
                        navigate(`/student-summary-printable/${student.id}`)
                      }
                    >
                      <i className="fas fa-print me-2"></i>
                      Print
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tab.Container
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
        >
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white">
              <Nav variant="tabs" className="border-0">
                <Nav.Item>
                  <Nav.Link eventKey="overview">
                    <i className="fas fa-home me-2"></i>Overview
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="attendance">
                    <i className="fas fa-calendar-check me-2"></i>Attendance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="academics">
                    <i className="fas fa-graduation-cap me-2"></i>Academics
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="installmentFees">
                    <i className="fas fa-coins me-2"></i>Fees
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="feeAdjustments">
                    <i className="fas fa-percent me-2"></i>Fee Adjustments
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="notes">
                    <i className="fas fa-sticky-note me-2"></i>Notes
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            <Card.Body className="p-4">
              <Tab.Content>
                {/* Overview Tab */}
                <Tab.Pane eventKey="overview">
                  <div className="row">
                    {/* Contact Info */}
                    <div className="col-md-4 col-sm-6 mb-4">
                      <Card className="h-100">
                        <Card.Header className="bg-light">
                          <h6 className="mb-0">
                            <i className="fas fa-address-card me-2"></i>Contact
                            Information
                          </h6>
                        </Card.Header>
                        <Card.Body>
                          {student.mobile && (
                            <div className="mb-3">
                              <strong>Mobile Numbers:</strong>
                              <div className="mt-2">
                                {student.mobile.split(',').map((num, idx) => (
                                  <div key={idx} className="d-flex gap-2 mb-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      href={`tel:${num.trim()}`}
                                    >
                                      <i className="fas fa-phone me-2"></i>
                                      {num.trim()}
                                    </Button>
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      href={`https://wa.me/${num.trim()}`}
                                      target="_blank"
                                    >
                                      <i className="fab fa-whatsapp"></i>
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {student.address && (
                            <div>
                              <strong>Address:</strong>
                              <div
                                className="mt-2 p-2 bg-light rounded"
                                dangerouslySetInnerHTML={{
                                  __html: (student.address || '').replace(
                                    /,/g,
                                    '<br/>',
                                  ),
                                }}
                              />
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>

                    {/* Batch History */}
                    <div className="col-md-4 col-sm-6 mb-4">
                      <Card className="h-100">
                        <Card.Header className="bg-light">
                          <h6 className="mb-0">
                            <i className="fas fa-history me-2"></i>Batch History
                          </h6>
                        </Card.Header>
                        <Card.Body>
                          <div
                            style={{ maxHeight: '300px', overflowY: 'auto' }}
                          >
                            {batchHistory.map((batch, idx) => {
                              const color = generateBatchColor(idx);
                              return (
                                <div
                                  key={batch.student_batch_id}
                                  className="mb-2 p-2 rounded"
                                  style={{
                                    background: color.bg,
                                    borderLeft: `4px solid ${color.border}`,
                                  }}
                                >
                                  <div className="d-flex justify-content-between">
                                    <strong style={{ color: color.text }}>
                                      {batch.class_name} {batch.division}
                                    </strong>
                                    {!batch.end_date && (
                                      <Badge bg="success">Current</Badge>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    {batch.assigned_date}{' '}
                                    {batch.end_date
                                      ? `to ${batch.end_date}`
                                      : 'to Present'}
                                  </small>
                                </div>
                              );
                            })}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </div>
                </Tab.Pane>

                {/* Attendance Tab */}
                <Tab.Pane eventKey="attendance">
                  <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
                    <h5 className="mb-2 mb-md-0">
                      <i className="fas fa-calendar-alt me-2"></i>Attendance
                      Calendar
                    </h5>
                    <div className="d-flex gap-2 align-items-center month-year-selector">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => changeMonth(-1)}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </Button>
                      <Form.Select
                        value={attendanceMonth.month}
                        onChange={(e) =>
                          setAttendanceMonth((prev) => ({
                            ...prev,
                            month: parseInt(e.target.value),
                          }))
                        }
                        className="month-select"
                        style={{ width: '150px' }}
                      >
                        {months.map((month, index) => (
                          <option key={index + 1} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="number"
                        value={attendanceMonth.year}
                        onChange={(e) =>
                          setAttendanceMonth((prev) => ({
                            ...prev,
                            year: parseInt(e.target.value),
                          }))
                        }
                        className="year-input"
                        style={{ width: '100px' }}
                      />
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => changeMonth(1)}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </Button>
                    </div>
                  </div>

                  {loadingAttendance ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" />
                    </div>
                  ) : (
                    renderEnhancedAttendanceCalendar()
                  )}
                </Tab.Pane>

                {/* Academics Tab */}
                <Tab.Pane eventKey="academics">
                  <Tab.Container defaultActiveKey="testMarks">
                    <Nav variant="pills" className="mb-3">
                      <Nav.Item>
                        <Nav.Link eventKey="testMarks">Test Marks</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="examMarks">Exam Marks</Nav.Link>
                      </Nav.Item>
                    </Nav>

                    <Tab.Content>
                      <Tab.Pane eventKey="testMarks">
                        {Object.keys(testMarks).length === 0 ? (
                          <p className="text-muted text-center">
                            No test marks available
                          </p>
                        ) : (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns:
                                'repeat(auto-fill, minmax(220px, 1fr))',
                              gap: '15px',
                            }}
                          >
                            {Object.keys(testMarks).map((subject) => (
                              <Card
                                key={subject}
                                style={{
                                  border: 'none',
                                  borderRadius: '10px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                }}
                              >
                                <Card.Header
                                  style={{
                                    background:
                                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    padding: '8px 12px',
                                    borderRadius: '10px 10px 0 0',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  {subject}
                                </Card.Header>
                                <Card.Body style={{ padding: '12px' }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '8px',
                                    }}
                                  >
                                    {testMarks[subject].map((test) => {
                                      const isGradeBased =
                                        test.evaluation_type === 'grade';
                                      const hasMark =
                                        test.mark && test.mark.trim() !== '';

                                      return (
                                        <div
                                          key={test.test_id}
                                          style={{
                                            padding: '10px 12px',
                                            border: isGradeBased
                                              ? '1px solid #f093fb'
                                              : '1px solid #667eea',
                                            borderRadius: '8px',
                                            background: isGradeBased
                                              ? 'linear-gradient(135deg, #fff5ff 0%, #ffe8f5 100%)'
                                              : 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            transition: 'all 0.2s ease',
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform =
                                              'translateX(3px)';
                                            e.currentTarget.style.boxShadow =
                                              '0 2px 8px rgba(0,0,0,0.1)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform =
                                              'translateX(0)';
                                            e.currentTarget.style.boxShadow =
                                              'none';
                                          }}
                                        >
                                          <div style={{ flex: 1 }}>
                                            <div
                                              style={{
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#2c3e50',
                                                marginBottom: '2px',
                                              }}
                                            >
                                              {test.test_name}
                                            </div>
                                            <div
                                              style={{
                                                fontSize: '0.7rem',
                                                color: '#7f8c8d',
                                              }}
                                            >
                                              {new Date(
                                                test.created_at,
                                              ).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                              })}
                                            </div>
                                          </div>

                                          <div
                                            style={{
                                              textAlign: 'right',
                                              minWidth: '80px',
                                            }}
                                          >
                                            {isGradeBased ? (
                                              <div>
                                                <div
                                                  style={{
                                                    fontSize: '1.4rem',
                                                    fontWeight: 'bold',
                                                    background:
                                                      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                    WebkitBackgroundClip:
                                                      'text',
                                                    WebkitTextFillColor:
                                                      'transparent',
                                                  }}
                                                >
                                                  {hasMark ? test.mark : '-'}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: '0.65rem',
                                                    fontWeight: '600',
                                                    color: '#f5576c',
                                                    textTransform: 'uppercase',
                                                  }}
                                                >
                                                  Grade
                                                </div>
                                              </div>
                                            ) : (
                                              <div>
                                                <div
                                                  style={{
                                                    fontSize: '1.3rem',
                                                    fontWeight: 'bold',
                                                    color: hasMark
                                                      ? '#667eea'
                                                      : '#ccc',
                                                  }}
                                                >
                                                  {hasMark ? test.mark : '-'}
                                                  {test.total_mark && (
                                                    <span
                                                      style={{
                                                        fontSize: '0.9rem',
                                                        color: '#95a5a6',
                                                      }}
                                                    >
                                                      /{test.total_mark}
                                                    </span>
                                                  )}
                                                </div>
                                                {test.total_mark &&
                                                  hasMark &&
                                                  (() => {
                                                    const markValue =
                                                      parseFloat(test.mark);
                                                    const percentage = !isNaN(
                                                      markValue,
                                                    )
                                                      ? (
                                                          (markValue /
                                                            test.total_mark) *
                                                          100
                                                        ).toFixed(0)
                                                      : '0';
                                                    return (
                                                      <div
                                                        style={{
                                                          fontSize: '0.7rem',
                                                          fontWeight: '600',
                                                          color: '#667eea',
                                                        }}
                                                      >
                                                        {percentage}%
                                                      </div>
                                                    );
                                                  })()}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </Card.Body>
                              </Card>
                            ))}
                          </div>
                        )}
                      </Tab.Pane>

                      <Tab.Pane eventKey="examMarks">
                        {loadingExamMarks ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </div>
                        ) : examTerms.length === 0 ? (
                          <div className="text-center py-5">
                            <i
                              className="fas fa-clipboard-list"
                              style={{
                                fontSize: '3rem',
                                color: '#dee2e6',
                                marginBottom: '1rem',
                              }}
                            ></i>
                            <p className="text-muted">
                              No exam marks available
                            </p>
                          </div>
                        ) : (
                          <div
                            className="exam-marks-grid"
                            style={{
                              display: 'grid',
                              gridTemplateColumns:
                                'repeat(auto-fill, minmax(400px, 1fr))',
                              gap: '20px',
                            }}
                          >
                            {examTerms.map((examInfo) => {
                              const isGradeBased =
                                examInfo.evaluation_type === 'grade';

                              return (
                                <Card
                                  key={examInfo.name}
                                  className="exam-marks-card"
                                  style={{
                                    border: 'none',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    overflow: 'hidden',
                                    transition:
                                      'transform 0.2s ease, box-shadow 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      'translateY(-4px)';
                                    e.currentTarget.style.boxShadow =
                                      '0 6px 20px rgba(102, 126, 234, 0.3)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      'translateY(0)';
                                    e.currentTarget.style.boxShadow =
                                      '0 4px 12px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <Card.Header
                                    style={{
                                      background:
                                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                      fontWeight: '600',
                                      fontSize: '1.1rem',
                                      padding: '16px 20px',
                                      borderBottom: 'none',
                                    }}
                                  >
                                    <i className="fas fa-graduation-cap me-2"></i>
                                    {examInfo.name}
                                    {isGradeBased && (
                                      <Badge
                                        bg="light"
                                        text="dark"
                                        className="ms-2"
                                        style={{ fontSize: '0.75rem' }}
                                      >
                                        Grade
                                      </Badge>
                                    )}
                                  </Card.Header>
                                  <Card.Body className="p-0">
                                    <Table
                                      responsive
                                      className="mb-0 exam-marks-table"
                                      style={{ fontSize: '0.95rem' }}
                                    >
                                      <thead>
                                        <tr style={{ background: '#f8f9fa' }}>
                                          <th
                                            style={{
                                              padding: '12px 20px',
                                              fontWeight: '600',
                                              color: '#495057',
                                              borderBottom: '2px solid #dee2e6',
                                            }}
                                          >
                                            Subject
                                          </th>
                                          {isGradeBased ? (
                                            <th
                                              style={{
                                                padding: '12px 20px',
                                                fontWeight: '600',
                                                color: '#495057',
                                                borderBottom:
                                                  '2px solid #dee2e6',
                                                textAlign: 'center',
                                                width: '30%',
                                              }}
                                            >
                                              Grade
                                            </th>
                                          ) : (
                                            <>
                                              <th
                                                style={{
                                                  padding: '12px 20px',
                                                  fontWeight: '600',
                                                  color: '#495057',
                                                  borderBottom:
                                                    '2px solid #dee2e6',
                                                  textAlign: 'center',
                                                  width: '25%',
                                                }}
                                              >
                                                Marks Obtained
                                              </th>
                                              <th
                                                style={{
                                                  padding: '12px 20px',
                                                  fontWeight: '600',
                                                  color: '#495057',
                                                  borderBottom:
                                                    '2px solid #dee2e6',
                                                  textAlign: 'center',
                                                  width: '25%',
                                                }}
                                              >
                                                Max Mark
                                              </th>
                                            </>
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {examSubjects
                                          .filter((subject) => {
                                            // Filter out Total rows for grade-based exams
                                            if (
                                              isGradeBased &&
                                              subject.subject_name === 'Total'
                                            ) {
                                              return false;
                                            }
                                            // Only show subjects that have data for this exam
                                            return subject.marks[examInfo.name];
                                          })
                                          .map(
                                            (
                                              {
                                                subject_name,
                                                marks,
                                                subject_id,
                                              },
                                              idx,
                                            ) => {
                                              const markData =
                                                marks[examInfo.name];
                                              const isTotal =
                                                subject_name === 'Total';

                                              return (
                                                <tr
                                                  key={subject_id}
                                                  style={{
                                                    background: isTotal
                                                      ? '#e7f3ff'
                                                      : idx % 2 === 0
                                                        ? '#fff'
                                                        : '#fafbfc',
                                                    transition:
                                                      'background 0.2s ease',
                                                    fontWeight: isTotal
                                                      ? '700'
                                                      : 'normal',
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!isTotal) {
                                                      e.currentTarget.style.background =
                                                        '#f0f4ff';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isTotal) {
                                                      e.currentTarget.style.background =
                                                        idx % 2 === 0
                                                          ? '#fff'
                                                          : '#fafbfc';
                                                    }
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      padding: '14px 20px',
                                                      color: isTotal
                                                        ? '#667eea'
                                                        : '#2c3e50',
                                                      borderBottom:
                                                        '1px solid #e9ecef',
                                                    }}
                                                  >
                                                    {subject_name}
                                                  </td>
                                                  {isGradeBased ? (
                                                    <td
                                                      style={{
                                                        padding: '14px 20px',
                                                        textAlign: 'center',
                                                        fontWeight: '600',
                                                        color:
                                                          !markData?.marks_obtained ||
                                                          markData.marks_obtained ===
                                                            '-'
                                                            ? '#adb5bd'
                                                            : '#667eea',
                                                        fontSize: '1rem',
                                                        borderBottom:
                                                          '1px solid #e9ecef',
                                                      }}
                                                    >
                                                      {markData?.marks_obtained ||
                                                        '-'}
                                                    </td>
                                                  ) : (
                                                    <>
                                                      <td
                                                        style={{
                                                          padding: '14px 20px',
                                                          textAlign: 'center',
                                                          fontWeight: '600',
                                                          color: isTotal
                                                            ? '#667eea'
                                                            : markData?.marks_obtained !==
                                                                null
                                                              ? '#2c3e50'
                                                              : '#adb5bd',
                                                          fontSize: '1rem',
                                                          borderBottom:
                                                            '1px solid #e9ecef',
                                                        }}
                                                      >
                                                        {markData?.marks_obtained ??
                                                          '-'}
                                                      </td>
                                                      <td
                                                        style={{
                                                          padding: '14px 20px',
                                                          textAlign: 'center',
                                                          fontWeight: '600',
                                                          color: isTotal
                                                            ? '#667eea'
                                                            : '#7f8c8d',
                                                          fontSize: '1rem',
                                                          borderBottom:
                                                            '1px solid #e9ecef',
                                                        }}
                                                      >
                                                        {markData?.max_mark ??
                                                          '-'}
                                                      </td>
                                                    </>
                                                  )}
                                                </tr>
                                              );
                                            },
                                          )}
                                      </tbody>
                                    </Table>
                                  </Card.Body>
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                </Tab.Pane>

                {/* Fees Tab */}
                <Tab.Pane eventKey="fees">
                  <Card className="mb-4">
                    <Card.Body className="p-2">
                      <Table bordered size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr className="text-center">
                            <th className="text-danger">Previous Balance</th>
                            <th className="text-primary">This Year Fee</th>
                            <th className="text-success">Total Paid</th>
                            {feeData.totalReturn > 0 && (
                              <th className="text-warning">Returned</th>
                            )}
                            <th className="text-info">Final Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="text-center fw-bold">
                            <td className="text-danger">
                              ₹{feeData.prevBalance}
                            </td>
                            <td className="text-primary">
                              ₹{feeData.thisYearFee}
                            </td>
                            <td className="text-success">
                              ₹{feeData.totalPaid}
                            </td>
                            {feeData.totalReturn > 0 && (
                              <td className="text-warning">
                                ₹{feeData.totalReturn}
                              </td>
                            )}
                            <td className="text-info">
                              ₹
                              {(
                                feeData.prevBalance +
                                feeData.thisYearFee -
                                feeData.totalPaid +
                                feeData.totalReturn
                              ).toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">Transaction History</h6>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table bordered hover size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Date</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.length === 0 ? (
                              <tr>
                                <td
                                  colSpan="2"
                                  className="text-center text-muted"
                                >
                                  No transactions
                                </td>
                              </tr>
                            ) : (
                              transactions.map((txn, i) => (
                                <tr
                                  key={i}
                                  className={
                                    txn.txn_type === 'C'
                                      ? 'table-success'
                                      : 'table-danger'
                                  }
                                >
                                  <td>{txn.particular}</td>
                                  <td className="text-end fw-bold">
                                    ₹{parseFloat(txn.amount).toFixed(2)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Installment Fees Tab */}
                <Tab.Pane eventKey="installmentFees">
                  {loadingInstallmentFees ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : installmentFeeData ? (
                    <div>
                      {/* Fee Summary Card */}
                      <Card className="mb-4">
                        <Card.Header className="bg-primary text-white">
                          <h5 className="mb-0">
                            <i className="fas fa-chart-pie me-2"></i>Fee Summary
                          </h5>
                        </Card.Header>
                        <Card.Body className="p-3">
                          <div className="row text-center">
                            <div className="col-md-3 col-6 mb-3">
                              <div className="p-3 bg-light rounded">
                                <h4 className="text-primary mb-1">
                                  ₹
                                  {installmentFeeData.total_fee?.toLocaleString()}
                                </h4>
                                <small className="text-muted">Total Fee</small>
                              </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                              <div className="p-3 bg-light rounded">
                                <h4 className="text-success mb-1">
                                  ₹
                                  {installmentFeeData.total_paid?.toLocaleString()}
                                </h4>
                                <small className="text-muted">Total Paid</small>
                              </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                              <div className="p-3 bg-light rounded">
                                <h4 className="text-danger mb-1">
                                  ₹
                                  {installmentFeeData.balance?.toLocaleString()}
                                </h4>
                                <small className="text-muted">Balance</small>
                              </div>
                            </div>
                            <div className="col-md-3 col-6 mb-3">
                              <div className="p-3 bg-light rounded">
                                <h4 className="text-info mb-1">
                                  {Math.round(
                                    (installmentFeeData.total_paid /
                                      installmentFeeData.total_fee) *
                                      100,
                                  )}
                                  %
                                </h4>
                                <small className="text-muted">Paid</small>
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-3">
                            <div
                              className="progress"
                              style={{ height: '10px' }}
                            >
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{
                                  width: `${(installmentFeeData.total_paid / installmentFeeData.total_fee) * 100}%`,
                                }}
                                aria-valuenow={
                                  (installmentFeeData.total_paid /
                                    installmentFeeData.total_fee) *
                                  100
                                }
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>

                      {/* Installments Card */}
                      <Card>
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">
                            <i className="fas fa-list-ol me-2"></i>Installments
                          </h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <Table className="mb-0" hover>
                              <thead className="table-light">
                                <tr>
                                  <th style={{ width: '60px' }}>#</th>
                                  <th>Title</th>
                                  <th className="text-end">Amount</th>
                                  <th className="text-end">Paid</th>
                                  <th className="text-end">Balance</th>
                                  <th className="text-center">Due Date</th>
                                  <th
                                    className="text-center"
                                    style={{ width: '100px' }}
                                  >
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {installmentFeeData.installments?.map(
                                  (installment) => {
                                    const getDueDateStatus = (
                                      dueDate,
                                      balance,
                                      paidAmount,
                                    ) => {
                                      const numBalance =
                                        parseFloat(balance) || 0;
                                      const numPaid =
                                        parseFloat(paidAmount) || 0;

                                      if (
                                        numBalance === 0 ||
                                        (numPaid > 0 && numBalance === 0)
                                      ) {
                                        return { type: 'paid', daysDiff: 0 };
                                      }

                                      const today = new Date();
                                      const due = new Date(dueDate);
                                      const timeDiff =
                                        due.getTime() - today.getTime();
                                      const daysDiff = Math.ceil(
                                        timeDiff / (1000 * 3600 * 24),
                                      );

                                      if (daysDiff < 0) {
                                        return {
                                          type: 'overdue',
                                          daysDiff: Math.abs(daysDiff),
                                        };
                                      } else if (daysDiff === 0) {
                                        return {
                                          type: 'due_today',
                                          daysDiff: 0,
                                        };
                                      } else if (daysDiff <= 7) {
                                        return { type: 'due_soon', daysDiff };
                                      } else {
                                        return { type: 'upcoming', daysDiff };
                                      }
                                    };

                                    const getStatusBadge = (
                                      status,
                                      balance,
                                      dueDate,
                                      paidAmount,
                                    ) => {
                                      const dueDateStatus = getDueDateStatus(
                                        dueDate,
                                        balance,
                                        paidAmount,
                                      );

                                      if (dueDateStatus.type === 'paid') {
                                        return <Badge bg="success">Paid</Badge>;
                                      } else if (
                                        dueDateStatus.type === 'overdue'
                                      ) {
                                        return (
                                          <div className="d-flex flex-column align-items-center">
                                            <Badge bg="danger">Overdue</Badge>
                                            <small
                                              className="text-danger"
                                              style={{ fontSize: '0.65rem' }}
                                            >
                                              {dueDateStatus.daysDiff} days ago
                                            </small>
                                          </div>
                                        );
                                      } else if (
                                        dueDateStatus.type === 'due_today'
                                      ) {
                                        return (
                                          <Badge bg="warning">Due Today</Badge>
                                        );
                                      } else if (
                                        dueDateStatus.type === 'due_soon'
                                      ) {
                                        return (
                                          <div className="d-flex flex-column align-items-center">
                                            <Badge bg="warning">Due Soon</Badge>
                                            <small
                                              className="text-warning"
                                              style={{ fontSize: '0.65rem' }}
                                            >
                                              in {dueDateStatus.daysDiff} days
                                            </small>
                                          </div>
                                        );
                                      } else {
                                        return (
                                          <div className="d-flex flex-column align-items-center">
                                            <Badge bg="secondary">
                                              Upcoming
                                            </Badge>
                                            <small
                                              className="text-muted"
                                              style={{ fontSize: '0.65rem' }}
                                            >
                                              in {dueDateStatus.daysDiff} days
                                            </small>
                                          </div>
                                        );
                                      }
                                    };

                                    const dueDateStatus = getDueDateStatus(
                                      installment.due_date,
                                      installment.balance,
                                      installment.paid,
                                    );

                                    return (
                                      <tr key={installment.installment_id}>
                                        <td>
                                          <div
                                            className="text-center fw-bold"
                                            style={{
                                              width: '30px',
                                              height: '30px',
                                              background:
                                                parseFloat(
                                                  installment.balance,
                                                ) === 0
                                                  ? '#d4edda'
                                                  : dueDateStatus.type ===
                                                      'overdue'
                                                    ? '#f8d7da'
                                                    : dueDateStatus.type ===
                                                          'due_today' ||
                                                        dueDateStatus.type ===
                                                          'due_soon'
                                                      ? '#fff3cd'
                                                      : '#f8f9fa',
                                              borderRadius: '50%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              border:
                                                parseFloat(
                                                  installment.balance,
                                                ) === 0
                                                  ? '2px solid #198754'
                                                  : dueDateStatus.type ===
                                                      'overdue'
                                                    ? '2px solid #dc3545'
                                                    : dueDateStatus.type ===
                                                          'due_today' ||
                                                        dueDateStatus.type ===
                                                          'due_soon'
                                                      ? '2px solid #ffc107'
                                                      : '2px solid #dee2e6',
                                            }}
                                          >
                                            {dueDateStatus.type ===
                                            'overdue' ? (
                                              <i
                                                className="fas fa-exclamation text-danger"
                                                style={{ fontSize: '12px' }}
                                              ></i>
                                            ) : dueDateStatus.type ===
                                                'due_today' ||
                                              dueDateStatus.type ===
                                                'due_soon' ? (
                                              <i
                                                className="fas fa-clock text-warning"
                                                style={{ fontSize: '12px' }}
                                              ></i>
                                            ) : (
                                              installment.installment_no
                                            )}
                                          </div>
                                        </td>
                                        <td>
                                          <div>
                                            <strong>{installment.title}</strong>
                                          </div>
                                        </td>
                                        <td className="text-end fw-bold text-primary">
                                          ₹
                                          {parseFloat(
                                            installment.amount || 0,
                                          ).toLocaleString()}
                                        </td>
                                        <td className="text-end fw-bold text-success">
                                          ₹
                                          {parseFloat(
                                            installment.paid || 0,
                                          ).toLocaleString()}
                                        </td>
                                        <td className="text-end fw-bold text-danger">
                                          ₹
                                          {parseFloat(
                                            installment.balance || 0,
                                          ).toLocaleString()}
                                        </td>
                                        <td className="text-center">
                                          <div className="d-flex flex-column align-items-center">
                                            <div
                                              className="text-dark fw-bold"
                                              style={{ fontSize: '0.85rem' }}
                                            >
                                              {new Date(
                                                installment.due_date,
                                              ).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                              })}
                                            </div>
                                            {dueDateStatus.type ===
                                              'overdue' && (
                                              <small
                                                className="text-danger"
                                                style={{ fontSize: '0.65rem' }}
                                              >
                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                {dueDateStatus.daysDiff} days
                                                overdue
                                              </small>
                                            )}
                                            {dueDateStatus.type ===
                                              'due_today' && (
                                              <small
                                                className="text-warning"
                                                style={{ fontSize: '0.65rem' }}
                                              >
                                                <i className="fas fa-clock me-1"></i>
                                                Due today!
                                              </small>
                                            )}
                                            {dueDateStatus.type ===
                                              'due_soon' && (
                                              <small
                                                className="text-warning"
                                                style={{ fontSize: '0.65rem' }}
                                              >
                                                <i className="fas fa-clock me-1"></i>
                                                Due in {dueDateStatus.daysDiff}{' '}
                                                days
                                              </small>
                                            )}
                                          </div>
                                        </td>
                                        <td className="text-center">
                                          {getStatusBadge(
                                            installment.status,
                                            installment.balance,
                                            installment.due_date,
                                            installment.paid,
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  },
                                )}
                              </tbody>
                              <tfoot className="table-light">
                                <tr className="fw-bold">
                                  <td colSpan="2" className="text-end">
                                    Totals:
                                  </td>
                                  <td className="text-end text-primary">
                                    ₹
                                    {installmentFeeData.installments
                                      ?.reduce(
                                        (sum, inst) =>
                                          sum + (parseFloat(inst.amount) || 0),
                                        0,
                                      )
                                      .toLocaleString()}
                                  </td>
                                  <td className="text-end text-success">
                                    ₹
                                    {installmentFeeData.installments
                                      ?.reduce(
                                        (sum, inst) =>
                                          sum + (parseFloat(inst.paid) || 0),
                                        0,
                                      )
                                      .toLocaleString()}
                                  </td>
                                  <td className="text-end text-danger">
                                    ₹
                                    {installmentFeeData.installments
                                      ?.reduce(
                                        (sum, inst) =>
                                          sum + (parseFloat(inst.balance) || 0),
                                        0,
                                      )
                                      .toLocaleString()}
                                  </td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>

                      <Card className="mt-4">
                        <Card.Header className="bg-light">
                          <h6 className="mb-0">
                            <i className="fas fa-receipt me-2"></i>
                            Transaction History
                          </h6>
                        </Card.Header>
                        <Card.Body>
                          <div
                            style={{ maxHeight: '400px', overflowY: 'auto' }}
                          >
                            <Table bordered hover size="sm" className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {transactions.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan="2"
                                      className="text-center text-muted"
                                    >
                                      No transactions
                                    </td>
                                  </tr>
                                ) : (
                                  transactions.map((txn, i) => (
                                    <tr
                                      key={i}
                                      className={
                                        txn.txn_type === 'C'
                                          ? 'table-success'
                                          : 'table-danger'
                                      }
                                    >
                                      <td>{txn.particular}</td>
                                      <td className="text-end fw-bold">
                                        ₹{parseFloat(txn.amount).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i
                        className="fas fa-coins"
                        style={{
                          fontSize: '3rem',
                          color: '#dee2e6',
                          marginBottom: '1rem',
                        }}
                      ></i>
                      <p className="text-muted">
                        No installment fee data available
                      </p>
                    </div>
                  )}
                </Tab.Pane>

                {/* Fee Adjustments Tab */}
                <Tab.Pane eventKey="feeAdjustments">
                  {loadingFeeAdjustments ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : feeAdjustmentData ? (
                    <div>
                      <Card>
                        <Card.Header className="bg-light">
                          <h5 className="mb-0">
                            <i className="fas fa-list me-2"></i>Adjustments
                          </h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <Table className="mb-0" hover>
                              <thead className="table-light">
                                <tr>
                                  <th>Title</th>
                                  <th className="text-center">Type</th>
                                  <th className="text-end">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {!feeAdjustmentData.adjustments ||
                                feeAdjustmentData.adjustments.length === 0 ? (
                                  <tr>
                                    <td
                                      colSpan="3"
                                      className="text-center text-muted py-4"
                                    >
                                      No fee adjustments available
                                    </td>
                                  </tr>
                                ) : (
                                  feeAdjustmentData.adjustments.map(
                                    (adjustment) => (
                                      <tr key={adjustment.id}>
                                        <td className="fw-semibold">
                                          {adjustment.title}
                                        </td>
                                        <td className="text-center">
                                          <Badge
                                            bg={
                                              adjustment.type === 'discount'
                                                ? 'danger'
                                                : 'secondary'
                                            }
                                          >
                                            {adjustment.type}
                                          </Badge>
                                        </td>
                                        <td className="text-end fw-bold">
                                          ₹
                                          {parseFloat(
                                            adjustment.amount || 0,
                                          ).toLocaleString()}
                                        </td>
                                      </tr>
                                    ),
                                  )
                                )}
                              </tbody>
                            </Table>
                          </div>
                          <div className="d-flex justify-content-end flex-wrap gap-2 p-3 border-top bg-light">
                            <Badge bg="danger" pill>
                              Discount: ₹
                              {parseFloat(
                                feeAdjustmentData.total_discount || 0,
                              ).toLocaleString()}
                            </Badge>
                            <Badge bg="secondary" pill>
                              Extra: ₹
                              {parseFloat(
                                feeAdjustmentData.total_extra_charge || 0,
                              ).toLocaleString()}
                            </Badge>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i
                        className="fas fa-percent"
                        style={{
                          fontSize: '3rem',
                          color: '#dee2e6',
                          marginBottom: '1rem',
                        }}
                      ></i>
                      <p className="text-muted">
                        No fee adjustment data available
                      </p>
                    </div>
                  )}
                </Tab.Pane>

                {/* Notes Tab */}
                <Tab.Pane eventKey="notes">
                  {batchNotes.length === 0 ? (
                    <div>
                      <div className="mb-3">
                        <Button
                          variant="primary"
                          onClick={() => {
                            setNoteDate('');
                            setCurrentNote('');
                            setEditingNoteId(null);
                            const currentBatch = batchNotes.find(
                              (b) => b.end_date === null,
                            );
                            setCurrentBatchId(
                              currentBatch
                                ? currentBatch.batch_id
                                : batchNotes[0]?.batch_id || null,
                            );
                            setShowNoteModal(true);
                          }}
                        >
                          <i className="fas fa-plus me-2"></i>Add Note
                        </Button>
                      </div>
                      <p className="text-muted text-center">
                        No notes added yet
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        gap: '20px',
                        overflowX: 'auto',
                        paddingBottom: '10px',
                      }}
                    >
                      {[...batchNotes]
                        .sort((a, b) => {
                          // Sort by end_date: null (current) comes first
                          if (a.end_date === null && b.end_date !== null)
                            return -1;
                          if (a.end_date !== null && b.end_date === null)
                            return 1;
                          return 0;
                        })
                        .map((batchNote, batchIndex) => {
                          const isActive = !batchNote.end_date;
                          // Create unique key using batch_id and period dates
                          const periodKey = `${batchNote.batch_id}-${
                            batchNote.assigned_date
                          }-${batchNote.end_date || 'current'}`;
                          return (
                            <div
                              key={periodKey}
                              style={{
                                minWidth: '300px',
                                width: 'calc(25% - 15px)',
                                flexShrink: 0,
                                opacity: isActive ? 1 : 0.6,
                                border: isActive
                                  ? '2px solid #28a745'
                                  : '1px solid #dee2e6',
                                borderRadius: '8px',
                                padding: '12px',
                                backgroundColor: isActive
                                  ? '#f8fff9'
                                  : '#f8f9fa',
                                transition: 'all 0.3s ease',
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                  {isActive && (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => {
                                        setNoteDate('');
                                        setCurrentNote('');
                                        setEditingNoteId(null);
                                        setCurrentBatchId(batchNote.batch_id);
                                        setShowNoteModal(true);
                                      }}
                                      style={{
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '0.75rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                      }}
                                      title={`Add note to ${batchNote.class_name} ${batchNote.division}`}
                                    >
                                      <i className="fas fa-plus"></i>
                                    </Button>
                                  )}
                                  <Badge
                                    bg={isActive ? 'success' : 'secondary'}
                                  >
                                    {batchNote.class_name} {batchNote.division}
                                  </Badge>
                                </div>
                                <div
                                  className="text-muted"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {batchNote.assigned_date}
                                  {batchNote.end_date
                                    ? ` to ${batchNote.end_date}`
                                    : ' (Current)'}
                                </div>
                              </div>

                              {batchNote.notes && batchNote.notes.length > 0 ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px',
                                  }}
                                >
                                  {batchNote.notes
                                    .filter((note) => {
                                      const noteDate = note.note_date;
                                      const assignedDate =
                                        batchNote.assigned_date;
                                      const endDate =
                                        batchNote.end_date || '9999-12-31';
                                      return (
                                        noteDate >= assignedDate &&
                                        noteDate <= endDate
                                      );
                                    })
                                    .map((note, index) => (
                                      <Card key={index}>
                                        <Card.Header className="bg-light d-flex justify-content-between align-items-center p-2">
                                          <strong
                                            style={{ fontSize: '0.85rem' }}
                                          >
                                            {note.note_date}
                                          </strong>
                                          <div className="d-flex gap-1">
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                              onClick={() => {
                                                setNoteDate(note.note_date);
                                                setCurrentNote(note.note);
                                                setEditingNoteId(note.id);
                                                setCurrentBatchId(
                                                  batchNote.batch_id,
                                                );
                                                setShowNoteModal(true);
                                              }}
                                              style={{
                                                padding: '2px 6px',
                                                fontSize: '0.75rem',
                                              }}
                                            >
                                              <i className="fas fa-edit"></i>
                                            </Button>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() =>
                                                handleDeleteNote(
                                                  note,
                                                  batchNote.batch_id,
                                                )
                                              }
                                              style={{
                                                padding: '2px 6px',
                                                fontSize: '0.75rem',
                                              }}
                                            >
                                              <i className="fas fa-trash"></i>
                                            </Button>
                                          </div>
                                        </Card.Header>
                                        <Card.Body
                                          style={{
                                            padding: '10px',
                                            fontSize: '0.9rem',
                                          }}
                                        >
                                          {note.note}
                                        </Card.Body>
                                      </Card>
                                    ))}
                                </div>
                              ) : batchNote.notes &&
                                batchNote.notes.filter((note) => {
                                  const noteDate = note.note_date;
                                  const assignedDate = batchNote.assigned_date;
                                  const endDate =
                                    batchNote.end_date || '9999-12-31';
                                  return (
                                    noteDate >= assignedDate &&
                                    noteDate <= endDate
                                  );
                                }).length === 0 ? (
                                <p
                                  className="text-muted"
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  No notes for this batch period
                                </p>
                              ) : null}
                            </div>
                          );
                        })}

                      {/* Other Notes Column - for notes that don't fall in any batch period */}
                      {(() => {
                        // Collect all notes from all batches
                        const allNotes = [];
                        batchNotes.forEach((batchNote) => {
                          (batchNote.notes || []).forEach((note) => {
                            allNotes.push({
                              ...note,
                              batch_id: batchNote.batch_id,
                              batch_name: `${batchNote.class_name} ${batchNote.division}`,
                            });
                          });
                        });

                        // Find notes that don't fall within any batch period
                        const otherNotes = allNotes.filter((note) => {
                          const noteDate = note.note_date;
                          // Check if this note falls within ANY batch period
                          const fallsInAnyPeriod = batchNotes.some(
                            (batchNote) => {
                              if (batchNote.batch_id !== note.batch_id)
                                return false;
                              const assignedDate = batchNote.assigned_date;
                              const endDate =
                                batchNote.end_date || '9999-12-31';
                              return (
                                noteDate >= assignedDate && noteDate <= endDate
                              );
                            },
                          );
                          return !fallsInAnyPeriod;
                        });

                        if (otherNotes.length === 0) return null;

                        return (
                          <div
                            style={{
                              minWidth: '300px',
                              width: 'calc(25% - 15px)',
                              flexShrink: 0,
                              opacity: 0.8,
                              border: '1px solid #ffc107',
                              borderRadius: '8px',
                              padding: '12px',
                              backgroundColor: '#fff8e1',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <Badge bg="warning" text="dark">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Other Notes
                                </Badge>
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: '0.75rem' }}
                              >
                                Not in any period
                              </div>
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                              }}
                            >
                              {otherNotes.map((note, index) => (
                                <Card key={index} className="border-warning">
                                  <Card.Header className="bg-light d-flex justify-content-between align-items-center p-2">
                                    <div>
                                      <strong style={{ fontSize: '0.85rem' }}>
                                        {note.note_date}
                                      </strong>
                                      <div
                                        style={{ fontSize: '0.7rem' }}
                                        className="text-muted"
                                      >
                                        {note.batch_name}
                                      </div>
                                    </div>
                                    <div className="d-flex gap-1">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                          setNoteDate(note.note_date);
                                          setCurrentNote(note.note);
                                          setEditingNoteId(note.id);
                                          setCurrentBatchId(note.batch_id);
                                          setShowNoteModal(true);
                                        }}
                                        style={{
                                          padding: '2px 6px',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                          handleDeleteNote(note, note.batch_id)
                                        }
                                        style={{
                                          padding: '2px 6px',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </Button>
                                    </div>
                                  </Card.Header>
                                  <Card.Body
                                    style={{
                                      padding: '10px',
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {note.note}
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
      </div>

      {/* Note Modal */}
      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Note
            {currentBatchId && batchNotes.length > 0 && (
              <>
                {' - '}
                <Badge bg="secondary">
                  {
                    batchNotes.find((b) => b.batch_id === currentBatchId)
                      ?.class_name
                  }{' '}
                  {
                    batchNotes.find((b) => b.batch_id === currentBatchId)
                      ?.division
                  }
                </Badge>
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {editingNoteId && (
                <Button
                  variant="danger"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Are you sure you want to delete this note?',
                      )
                    )
                      return;

                    const token = getAuthToken();
                    try {
                      await axios.post(
                        `${appConstants.API_ROOT_URL}create-student-note-v2`,
                        {
                          student_id: parseInt(studentId),
                          batch_id: currentBatchId,
                          date: noteDate,
                          note: '',
                        },
                        { headers: { Authorization: `Bearer ${token}` } },
                      );

                      const response = await axios.post(
                        `${appConstants.API_ROOT_URL}get-one-student-notes-v2`,
                        { student_id: studentId },
                        { headers: { Authorization: `Bearer ${token}` } },
                      );
                      const batch_notes = response.data.batch_notes || [];
                      setBatchNotes(batch_notes);

                      const flattenedNotes = [];
                      batch_notes.forEach((batchNote) => {
                        (batchNote.notes || []).forEach((note) => {
                          flattenedNotes.push({
                            ...note,
                            batch_id: batchNote.batch_id,
                            class_name: batchNote.class_name,
                            division: batchNote.division,
                          });
                        });
                      });
                      setNotes(flattenedNotes);
                      setShowNoteModal(false);
                    } catch (error) {
                      console.error('Failed to delete note', error);
                      const errorMessage =
                        error.response?.data?.message ||
                        error.message ||
                        'Failed to delete note';
                      alert(errorMessage);
                    }
                  }}
                >
                  <i className="fas fa-trash me-2"></i>Delete
                </Button>
              )}
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={() => setShowNoteModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveNote}>
                Save
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .calendar-cell.today {
          box-shadow: 0 0 0 2px #2196f3 inset;
        }

        /* Prevent horizontal overflow */
        .batch-calendars-grid {
          max-width: 100%;
          overflow-x: hidden;
        }

        .batch-calendar-container {
          max-width: 100%;
          box-sizing: border-box;
        }

        .calendar-grid,
        .calendar-day-headers {
          max-width: 100%;
          box-sizing: border-box;
        }

        .exam-marks-grid {
          max-width: 100%;
          overflow-x: hidden;
        }

        .exam-marks-card {
          max-width: 100%;
          box-sizing: border-box;
        }

        /* Desktop: 3 calendars per row */
        .batch-calendars-grid {
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        @media (max-width: 900px) {
          .batch-calendars-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .batch-calendars-grid {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
        }

        /* ...existing code... */
      `}</style>
    </Layout>
  );
};

export default StudentSummaryV3;
